import React, { memo } from 'react';

import { deprecate } from 'react-is-deprecated';
import { useSelector } from 'react-redux';

import PropTypes from 'prop-types';

import _isEqual from 'lodash/isEqual';

import { createChainedFunction } from '@atc/create-chained-function';

import { getAlphaClickType } from '@/utilities/getAlphaTaggingType';

import AlphaModule from '@/modules/AlphaModule.js';

import AlphaShowcaseInfoText from '@/components/AlphaShowcaseInfoText/index.js';
import InventoryAlphaListing from '@/components/InventoryAlphaListing/index.js';
import OwnerAlphaListing from '@/components/OwnerAlphaListing/index.js';

function AlphaShowcase({
    carouselSettings = {},
    emailOwnerProps = {},
    enableMyWallet,
    fireTelmetricsTag = () => { }, // deprecated
    isExactMatch = true,
    lazyLoad = false,
    listing = {},
    listingCTA = {},
    listings = [],
    listingsCount = 0,
    onActiveDealDetailsClick = () => { },
    onAlphaListingClick = () => { },
    onAlphaWalletClick = () => { },
    onCheckStatusClick = () => { },
    onCollapsibleHeaderClick = () => { },
    onHideAlpha = () => { },
    onInventoryListingClick = () => { },
    onPaymentCTAClick,
    onPricingDetailClick = () => { },
    onSaveToggle = () => { },
    onSettle = () => { },
    onTileClick = () => { },
    onViewOwnerInventoryClick = () => { },
    orientation = 'landscape',
    owner = {},
    parentId,
    personalPaymentProps = {},
    phoneClickHandler = () => { },
    savedListingIds = [],
    showAlphaPaymentRibbon = {},
    showCompareButton = false,
    uiContext = 'inventory',
    updateListingPayments = () => { },
}) {
    const isAlphaMarket = useSelector(AlphaModule.duck.selectors.isAlphaMarket);

    phoneClickHandler = createChainedFunction(phoneClickHandler, fireTelmetricsTag);

    const renderDealerAlpha = (
        <OwnerAlphaListing
            phoneClickHandler={phoneClickHandler}
            owner={owner}
            orientation={orientation}
            emailOwnerProps={emailOwnerProps}
            onInventoryListingClick={onInventoryListingClick}
            onTileClick={onTileClick}
            onViewOwnerInventoryClick={onViewOwnerInventoryClick}
            parentId={parentId}
            uiContext={uiContext}
            listingsCount={listingsCount}
            listings={listings}
            clickType={getAlphaClickType(isAlphaMarket, true)}
        />
    );

    const renderInventoryAlphaListing = (
        <InventoryAlphaListing
            carouselSettings={carouselSettings}
            emailOwnerProps={emailOwnerProps}
            enableMyWallet={enableMyWallet}
            isActiveDeal={false}
            isAlphaMarket={isAlphaMarket}
            isExactMatch={isExactMatch}
            isSaved={savedListingIds.includes(({ ...listing }).id)}
            key="alphaShowcaseBody"
            listing={listing}
            listings={listings}
            listingCTA={listingCTA}
            listingsCount={listingsCount}
            onActiveDealDetailsClick={onActiveDealDetailsClick}
            onAlphaListingClick={onAlphaListingClick}
            onCheckStatusClick={onCheckStatusClick}
            onCollapsibleHeaderClick={onCollapsibleHeaderClick}
            onInventoryListingClick={onInventoryListingClick}
            onAlphaWalletClick={onAlphaWalletClick}
            onPaymentCTAClick={onPaymentCTAClick}
            onPricingDetailClick={onPricingDetailClick}
            onSaveToggle={onSaveToggle}
            onSettle={onSettle}
            onTileClick={onTileClick}
            onViewOwnerInventoryClick={onViewOwnerInventoryClick}
            orientation={orientation}
            owner={owner}
            parentId={parentId}
            personalPaymentProps={personalPaymentProps}
            phoneClickHandler={phoneClickHandler}
            savedListingIds={savedListingIds}
            showAlphaPaymentRibbon={showAlphaPaymentRibbon}
            showCompareButton={showCompareButton}
            updateListingPayments={updateListingPayments}
        />
    );

    const renderAlphaShowCase = () => {
        let alpha = null;
        switch (uiContext) {
            case 'inventory':
                alpha = renderInventoryAlphaListing;
                break;
            case 'owner':
                alpha = renderDealerAlpha;
                break;
            default:
                break;
        }

        return (
            <>
                <AlphaShowcaseInfoText
                    dealerName={owner.name}
                    onHideAlpha={onHideAlpha}
                    showcaseListing={listing}
                    uiContext={uiContext}
                />
                {alpha}
            </>
        );
    };

    return (
        <div
            data-cmp="alphaShowcase"
            data-qaid="cntnr-alpha"
        >
            {uiContext ? renderAlphaShowCase() : null}
        </div>
    );

}

AlphaShowcase.propTypes = {
    /**
     * handler for carouselSettings in carousel
     */
    carouselSettings: PropTypes.object,
    /**
     * The emailOwnerProps Object has the props to pass through to the ConnectedEmailOwner component.
     */
    emailOwnerProps: PropTypes.object,
    /**
     * Whether to enable My Wallet functionality
     */
    enableMyWallet: PropTypes.bool,
    /**
      * The callback function fires when text or call CTA is clicked
      */
    fireTelmetricsTag: deprecate(PropTypes.func, 'fireTelmetricsTag is deprecated in OwnerDetails. Use phoneClickHandler instead'),
    /**
     * Whether the alpha listing matches the exact criteria
     */
    isExactMatch: PropTypes.bool,
    /**
     * When true, the image will load when in the viewport
     */
    lazyLoad: PropTypes.bool,
    /**
     * The primary listing for InventoryAlphaListing
     */
    listing: PropTypes.object,
    /**
     * The listingCTA object has attributes passed to the anchor.
     */
    listingCTA: PropTypes.object,
    /**
     * Listings to be passed to InventoryAlphaListing Carousel
     */
    listings: PropTypes.array,
    /**
     * Total count of vehicles in stock
     */
    listingsCount: PropTypes.number,
    /**
     * Callback for active deal details click
     */
    onActiveDealDetailsClick: PropTypes.func,
    /**
     * Handler for image click in alpha listing
     */
    onAlphaListingClick: PropTypes.func,
    /**
     * handler for Alpha Wallet Update click
     */
    onAlphaWalletClick: PropTypes.func,
    /**
     * Callback for check status click
     */
    onCheckStatusClick: PropTypes.func,
    /**
     * handler for collapsible header click
     */
    onCollapsibleHeaderClick: PropTypes.func,
    /**
     * Callback to hide alpha showcase
     */
    onHideAlpha: PropTypes.func,
    /**
     * Handler for inventory click in carousel
     */
    onInventoryListingClick: PropTypes.func,
    /**
     * Callback for payment CTA click
     */
    onPaymentCTAClick: PropTypes.func,
    /**
     * The callback function to handle pricing details link
     */
    onPricingDetailClick: PropTypes.func,
    /**
     * Toggle handler for save functionality on InventoryAlphaListing
     */
    onSaveToggle: PropTypes.func,
    /**
     * Handler for callback when inventory carousel settles after load or arrow click
     */
    onSettle: PropTypes.func,
    /**
     * Handler for owner tile in alpha listing
     */
    onTileClick: PropTypes.func,
    /**
     * Handler for View Owner Inventory button on Owner state or View Owner Inventory link in default state
     */
    onViewOwnerInventoryClick: PropTypes.func,
    /**
     * The device orientation ( can be used with featureFlags to control
     * options like view dealer inventory )
     */
    orientation: PropTypes.oneOf([
        'portrait',
        'landscape',
    ]),
    /**
     * The owner details object
     */
    owner: PropTypes.object,
    /**
     * analytics parameter
     */
    parentId: PropTypes.string,
    /**
     * Object for personal payment
     */
    personalPaymentProps: PropTypes.object,
    /**
     * The callback function fires when owner phone is called, replacing fireTelmetricsTag
     */
    phoneClickHandler: PropTypes.func,
    /**
     * All saved listings in redux
     */
    savedListingIds: PropTypes.array,
    /**
     * The showPaymentRibbon boolean to show/hide payment ribbon
     */
    showAlphaPaymentRibbon: PropTypes.bool,
    /**
     * Whether to show compare button
     */
    showCompareButton: PropTypes.bool,
    /**
     * Notifies the AlphaShowcase whether to display owner or invertory Alpha listing
     */
    uiContext: PropTypes.oneOf([
        'owner',
        'inventory',
    ]),
    /**
     * The callback function to handle updateListingPayments sction
     */
    updateListingPayments: PropTypes.func,
};

export default memo(AlphaShowcase, _isEqual);
